import { BodyText } from '@/components/BodyText';
import { InputBox } from '@/components/Inputs/InputBox';
import { NumberInput } from '@/components/Inputs/NumberInput';
import { MarkedSlider } from '@/components/MarkedSlider';
import { useSearchFilters } from '@/hooks/useSearchFilters';
import { Box, styled } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';

const ThresholdContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  padding: '8px 0 2px 0',
});

const HeaderContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '0 8px 0 0',
});

const SliderContainer = styled(Box)({
  display: 'flex',
  alignItems: 'flex-end',
  position: 'relative',
  gap: '12px',
  padding: '8px 8px 8px 14px',
});

export const ThresholdSliderSection = ({ label, testId }: { label: string; testId: string }) => {
  const { minThreshold, updateSearchParameters } = useSearchFilters();
  const [localThreshold, setLocalThreshold] = useState(minThreshold);
  const debounceReference = useRef<NodeJS.Timeout | undefined>(undefined);

  useEffect(() => {
    if (minThreshold !== localThreshold && !debounceReference.current) {
      setLocalThreshold(minThreshold);
    }
  }, [minThreshold, localThreshold]);

  const debouncedHandleThresholdChange = useCallback(
    (newValue: number) => {
      if (debounceReference.current) {
        clearTimeout(debounceReference.current);
      }
      debounceReference.current = setTimeout(() => {
        updateSearchParameters({ threshold: newValue });
      }, 300);
    },
    [updateSearchParameters]
  );

  const handleThresholdChange = (newValue: number) => {
    setLocalThreshold(newValue);
    debouncedHandleThresholdChange(newValue);
  };

  return (
    <ThresholdContainer data-testid={testId}>
      <HeaderContainer>
        <BodyText color='text.secondary'>{label}</BodyText>
      </HeaderContainer>
      <InputBox>
        <SliderContainer>
          <MarkedSlider
            sx={{
              minWidth: '120px',
              '& .MuiSlider-mark': { backgroundColor: 'secondary.main' },
              '& .MuiSlider-track': { backgroundColor: 'secondary.main' },
              '& .MuiSlider-rail': { backgroundColor: 'divider' },
            }}
            min={0}
            max={100}
            value={localThreshold}
            onChange={(_, newValue) => handleThresholdChange(newValue as number)}
            valueLabelDisplay='off'
          />
          <NumberInput
            value={localThreshold}
            onChange={handleThresholdChange}
            min={0}
            max={100}
            decimalPlaces={2}
            slotProps={{ input: { endAdornment: <BodyText>%</BodyText>, style: { fontSize: '14px' } } }}
          />
        </SliderContainer>
      </InputBox>
    </ThresholdContainer>
  );
};
